import React, { useState, useCallback } from 'react'
import { useNewTranslation, NewTrans } from 'hooks/index'
import { theme } from 'theme'
import { Box, Flex, Text } from 'rebass'
import Icon from 'components/Icon/Icon'
import styled from 'particles/themes/styled'
import RegistrationsListApproval from './RegistrationsListApproval'
import Progress from 'components/Shared/Progress'
import MassImportResult from 'components/Shared/MassImportResult'
import { useDispatch, useSelector } from 'react-redux'
import { closeModal, GLOBAL_MODAL_TYPE, selectIsModalOpen, selectModalCount } from 'state/constants'
import { selectRegistrationMassApproveSuccess, selectRegistrationMassApproveFailed } from 'state/registrations'
import ModalStyledPopup from 'components/ModalStyledPopup'
import { selectRegistrationRemainingSlots, selectRegistrationMassApproveProgress } from 'state/registrations'
import Modal from 'particles/Modal'

export enum ActionType {
    APPROVE,
    APPROVE_EXCEED,
    WAITLIST,
    PAYOUT,
}

const PopupContainer = styled(Box)`
    overflow: hidden;
`

interface ListApprovalModalProps {
    refreshList?: () => void
    onClick?: () => void
}

const ListApprovalModal: React.FC<ListApprovalModalProps> = ({ refreshList }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const [stepIndex, setStepIndex] = useState(1)
    const isApproveModalOpen = useSelector(selectIsModalOpen(GLOBAL_MODAL_TYPE.MASS_APPROVAL))
    const isWaitlistModalOpen = useSelector(
        selectIsModalOpen(GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_WAITLIST_MASS_APPROVAL),
    )
    const isApproveExceedModalOpen = useSelector(
        selectIsModalOpen(GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_MASS_APPROVAL_EXCEED),
    )

    const successRows = useSelector(selectRegistrationMassApproveSuccess)
    const failedRows = useSelector(selectRegistrationMassApproveFailed)

    const interestRegistrationRemainingSlots = useSelector(selectRegistrationRemainingSlots)
    const { count, total } = useSelector(selectRegistrationMassApproveProgress)
    const selectCount = useSelector(selectModalCount)
    const isOpen = isWaitlistModalOpen || isApproveModalOpen || isApproveExceedModalOpen
    const actionType = isWaitlistModalOpen
        ? ActionType.WAITLIST
        : isApproveExceedModalOpen
          ? ActionType.APPROVE_EXCEED
          : ActionType.APPROVE

    const headerColor = isWaitlistModalOpen ? theme.colors.orange : theme.colors.governorBay
    const onCloseModal = useCallback(() => dispatch(closeModal()), [])

    const moveToStep = useCallback(
        step => {
            if (stepIndex > 3) {
                return
            }
            setStepIndex(step)
        },
        [setStepIndex],
    )

    const onResetModal = useCallback(() => {
        if (refreshList && successRows.length > 0) refreshList()
        moveToStep(1)
        onCloseModal()
    }, [successRows])
    return (
        <>
            {actionType === ActionType.APPROVE_EXCEED ? (
                isOpen && (
                    <Modal
                        title={newT('modalWindow.notEnoughApprovalSlotsTitle')}
                        hasSecondaryButton={false}
                        primaryButtonProps={{
                            label: newT('button.gotIt'),
                        }}
                        size={isApproveExceedModalOpen ? 'm' : 'l'}
                        icon="warning"
                        onConfirm={onCloseModal}
                        onClose={onCloseModal}
                    >
                        <NewTrans i18nKey="submission.importList.exceedRemainingRegistrationSlots">
                            <Text fontSize="16px" lineHeight="24px">
                                You’re attempting to approve <b>{{ selectCount }} Partiposters</b> with only{' '}
                                <b>{{ interestRegistrationRemainingSlots }} remaining slot(s)</b> available. Please only
                                approve within the number of remaining slots available for your campaign.
                            </Text>
                            <Text color={theme.colors.mineShaft} fontSize="14px" textAlign="center">
                                When you no longer have any remaining slots, you can choose to move Partiposters to the
                                waitlist. Partiposters that have been added to the waitlist will automatically be
                                approved whenever a slot becomes available.
                            </Text>
                        </NewTrans>
                    </Modal>
                )
            ) : (
                <ModalStyledPopup
                    open={isOpen}
                    width={isApproveExceedModalOpen ? '509px' : '773px'}
                    closeOnDocumentClick={false}
                >
                    <PopupContainer m={theme.spacing.s} height={stepIndex === 2 ? undefined : '100%'}>
                        <>
                            <Flex justifyContent="center" paddingBottom={theme.spacing.xxxs}>
                                <Icon.TermsAndCondition fill={headerColor} />
                            </Flex>
                            <Text color={headerColor} fontSize="24px" textAlign="center" mb={theme.spacing.xxxxs}>
                                {isWaitlistModalOpen
                                    ? newT('submission.importList.importWaitlistPageTitle')
                                    : newT('submission.importList.importApprovalListPageTitle')}
                            </Text>
                            {stepIndex === 1 ? (
                                <RegistrationsListApproval
                                    close={onCloseModal}
                                    moveToStep={moveToStep}
                                    actionType={actionType}
                                />
                            ) : stepIndex === 2 ? (
                                <Progress count={count} total={total} />
                            ) : (
                                <MassImportResult
                                    close={onResetModal}
                                    actionType={actionType}
                                    successRows={successRows}
                                    failedRows={failedRows}
                                />
                            )}
                        </>
                    </PopupContainer>
                </ModalStyledPopup>
            )}
        </>
    )
}

export default ListApprovalModal
